import { Component, computed, DestroyRef, effect, inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { IonRouterOutlet, IonicModule, ModalController } from '@ionic/angular';
import { DesktopHeaderComponent } from 'src/app/general-components/desktop-header/desktop-header.component';
import { ActivatedRoute } from '@angular/router';
import { TicketInputComponent } from 'src/app/general-components/ticket-input/ticket-input.component';
import { FormControl, FormControlStatus, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Ticket } from 'src/app/interfaces/ticket';
import { Departure } from 'src/app/interfaces/departure';
import { iUser } from 'src/app/interfaces/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Booking } from 'src/app/interfaces/booking';
import { TourService } from 'src/app/services/tour.service';
import { LoadscreenComponent } from 'src/app/general-components/loadscreen/loadscreen.component';
import { tour } from 'src/app/interfaces/tour';
import { BookingSummaryComponent } from 'src/app/general-components/booking-summary/booking-summary.component';
import { Seat } from 'src/app/interfaces/seat';
import { selectedTicketsValidator } from 'src/app/validators/selected-tickets.validator';
import { InfobarComponent } from 'src/app/general-components/infobar/infobar.component';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { PackService } from 'src/app/services/pack.service';
import { Pack } from 'src/app/interfaces/pack';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-booking',
  templateUrl: './booking.page.html',
  styleUrls: ['./booking.page.scss'],
  standalone: true,
  imports: [
    IonicModule,
    DesktopHeaderComponent,
    TicketInputComponent,
    ReactiveFormsModule,
    LoadscreenComponent,
    InfobarComponent,
    DatePipe
  ]
})
export class BookingPage implements OnInit {

  destroyRef = inject(DestroyRef)

  // Tour id
  public id: string;

  public isLoading: boolean = true
  public isSubmitting: boolean = false
  public isPackSaved: boolean = false
  public bookingComplete: boolean = false

  public calendarMinDate: string = new Date().toISOString()
  public calendarSelectedDate: string = new Date().toISOString()

  public tour: tour = {} as tour
  public departures: Departure[] = []
  public hasDepartures: boolean = false
  private booking: Booking = {} as Booking

  public departureTimes: any = []
  public departureDayOptions: any = []
  public departureTimeOptions: any = []
  public departureSeatOptions: Seat[] = []
  public selectedDeparture: Departure = {} as Departure

  public selectedTickets: Ticket[] = []
  public totalPrice: number = 0

  public tourForm: FormGroup = new FormGroup({
    id: new FormControl(''),
    user_id: new FormControl('', Validators.required),
    tour_id: new FormControl('', Validators.required),
    date: new FormControl('', Validators.required),
    time: new FormControl('', Validators.required),
    tickets: new FormControl([], [Validators.required, selectedTicketsValidator()])
  })

  public packForm: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    pack_id: new FormControl('')
  })

  public packSearchForm: FormGroup = new FormGroup({
    searchText: new FormControl('')
  })


  public user: Signal<iUser> = computed(() => this.authService.user());
  public currentPacks: Signal<Pack[]> = computed(() => this.packService.currentPacks());
  public packSearchText: WritableSignal<string> = signal('');
  public filteredPacks: Signal<Pack[]> = computed(() => {
    const searchText = this.packSearchText();
    if (!searchText || searchText.trim().length === 0) {
      return this.currentPacks();
    }
    return this.packService.searchPacks(searchText);
  });

  isInvalidTicket: boolean = false // shoehorn fix

  userData = effect(() => {
    this.tourForm.controls['user_id'].setValue(this.user()?.ID);
  });

  isDateAllowed = (dateString: string): boolean => {
    if (this.departureDayOptions.length === 0) {
      return false;
    }
    return this.departureDayOptions.includes(dateString);
  };

  /**
   * Set default departure (next available departure)
   * Author: Cursor
   */
  private setDefaultDeparture(): void {
    const nextDeparture = this.departures
      .filter(departure => new Date(departure.isoDateTime) >= new Date())
      .sort((a, b) => new Date(a.isoDateTime).getTime() - new Date(b.isoDateTime).getTime())[0];

    if (nextDeparture) {
      this.calendarMinDate = nextDeparture.isoDateTime;
      this.calendarSelectedDate = nextDeparture.isoDateTime;
      this.tourForm.controls['date'].setValue(nextDeparture.isoDateTime.split('T')[0]);
    }
  }


  formatDate(event: CustomEvent) {
    const value = event.detail.value;
    if (value) {
      // Formatér dato til 'YYYY-MM-DD'
      this.tourForm.controls['date'].setValue(value.split('T')[0]);
    }
  }

  formValid: WritableSignal<boolean> = signal(false)
  ticketsValid: WritableSignal<boolean> = signal(false)

  /**
   * Represents the overall validity state based on all validation sources.
   */
  validityState: Signal<boolean> = computed(() => {
    const FORM_VALID = this.formValid()
    const TICKETS_VALID = this.ticketsValid()

    return FORM_VALID && TICKETS_VALID
  })

  constructor(
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private tourService: TourService,
    private modalController: ModalController,
    private routerOutlet: IonRouterOutlet,
    public packService: PackService
  ) { }


  ngOnInit(): void {
    
    // Get the tour data
    this.route.paramMap.subscribe(params => {
      this.id = params.get('tourId') || '';
      this.tourForm.controls['tour_id'].setValue(this.id);

      this.tourService.getTourById(this.id).subscribe({
        next: (tour: any) => {
          this.tour = tour.result as tour;

          // Add Arctic Excursions to tours without valid ticket types 
          if (!this.tour.valid_ticket_types) {
            this.tour.valid_ticket_types = [];
          }
          if (this.tour.valid_ticket_types.length === 0) {
            this.tour.valid_ticket_types.push({ slug: "arctic_excursions", name: "Arctic Excursions" });
          }

          this.departures = tour.result.departures || [];

          if (this.departures.length > 0) {
            this.hasDepartures = true;

            const today = new Date().toISOString().split('T')[0];
            this.departureDayOptions = Array.from(new Set(this.departures
              .map(departure => departure.isoDateTime.split('T')[0])
              .filter(date => date >= today)));

            // Check for query parameters to pre-fill form
            this.route.queryParams.subscribe(queryParams => {
              if (queryParams['date'] && queryParams['time']) {
                // Find matching departure based on date and time
                const matchingDeparture = this.departures.find(departure => {
                  const departureDate = departure.isoDateTime.split('T')[0]; // YYYY-MM-DD
                  const departureTime = departure.time; // HH:MM
                  return departureDate === queryParams['date'] && departureTime === queryParams['time'];
                });

                if (matchingDeparture) {
                  // Pre-fill form with found departure
                  this.tourForm.controls['date'].setValue(queryParams['date']);
                  this.tourForm.controls['time'].setValue(queryParams['time']);
                  this.tourForm.controls['id'].setValue(matchingDeparture.id);
                  this.calendarSelectedDate = queryParams['date'] + 'T00:00:00';
                  this.calendarMinDate = matchingDeparture.isoDateTime;
                } else {
                  console.warn('No matching departure found for date:', queryParams['date'], 'time:', queryParams['time']);
                  // Fall back to default behavior
                  this.setDefaultDeparture();
                }
              } else if (queryParams['date'] && queryParams['time'] && queryParams['departureId']) {
                // Legacy support: Pre-fill form with query parameters including departureId
                this.tourForm.controls['date'].setValue(queryParams['date']);
                this.tourForm.controls['time'].setValue(queryParams['time']);
                this.tourForm.controls['id'].setValue(queryParams['departureId']);
                this.calendarSelectedDate = queryParams['date'] + 'T00:00:00';
              } else {
                // Use default next departure if no query parameters
                this.setDefaultDeparture();
              }
            });
          } else {
            this.hasDepartures = false;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching tour data:', err);
          this.isLoading = false;
        }
      });
    });

    // Update the time options on date change
    this.tourForm.get('date')?.valueChanges.subscribe(date => {
      this.departureTimeOptions = this.departures.filter(departure => departure.isoDateTime.split('T')[0] === date.split('T')[0]);
      this.calendarSelectedDate = date;
    });

    // Update the seat options on time (departure) change
    this.tourForm.get('id')?.valueChanges.subscribe(id => {
      this.selectedDeparture = this.departures.find(departure => departure.id === id);

      if (this.selectedDeparture) {
        this.resetTickets();
        // Load current seat options
        if (this.selectedDeparture && this.selectedDeparture.seats) {
          this.departureSeatOptions = [...this.selectedDeparture.seats].sort((a, b) => a.name.localeCompare(b.name));
          this.tourForm.controls['time'].setValue(this.selectedDeparture.time);
        }
      }
    });

    this.tourForm.controls['tickets'].valueChanges.subscribe(() => {
      this.totalPrice = this.selectedTickets.length * this.tour.price;
    });

    // Load all user packs
    this.packService.loadAllUsersPacks();

    // Listen for pack search changes
    this.packSearchForm.get('searchText')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((searchText: string) => {
        this.packSearchText.set(searchText || '');
      });

    // projects validity of `tourForm` into signal
    this.tourForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: FormControlStatus) => {
        this.formValid.set(status === 'VALID')
      })
  }

  /**
   * Check if a seat is selected
   * @param slug 
   * @return
   */
  hasSelectedSeat(slug: string): boolean {
    return this.selectedTickets.some((t: Seat) => t.slug === slug);
  }


  /**
   * Submit the tour form and dispatch a custom event with the response status
   * @return
   */
  public async confirmTourBooking() {
    this.isSubmitting = true
    this.bookingComplete = true
    this.booking = this.tourForm.value as Booking;

    // Validate the booking object
    if (!this.booking.id || !this.booking.user_id || !this.booking.tour_id || !this.booking.date || !this.booking.time || !this.booking.tickets.length) {
      console.error('Form validation failed', this.booking);
      this.isSubmitting = false;
      return false;
    }

    const modal = await this.modalController.create({
      component: BookingSummaryComponent,
      componentProps: {
        booking: this.booking,
        tour: this.tour,
        showControls: true,
      },
      backdropDismiss: false,
      canDismiss: true,
      presentingElement: this.routerOutlet.nativeEl
    });

    return await modal.present();
  }


  /**
   * Reset the form and variables
   */
  public resetForm() {
    this.tourForm.reset()
    this.selectedTickets = []

    // Reset the tour id
    this.tourForm.controls['tour_id'].setValue(this.id)
  }


  /**
   * Get the total amount of tickets selected
   * @return
   */
  public getSelectedTicketCount(ticket_type: string) {
    return this.selectedTickets.filter(ticket => ticket.ticket_type === ticket_type).length;
  }


  /**
   * Add a new blank ticket
   */
  public addTicket(ticket: Ticket = {} as Ticket) {

    if (ticket && ticket.ticket_type) {
      const originalTicket = this.departureSeatOptions.find(t => t.slug === ticket.ticket_type);
      if (originalTicket) {
        originalTicket.available_seats -= 1;
      }
    }

    this.selectedTickets.push(ticket)
    this.tourForm.controls['tickets'].setValue(this.selectedTickets)

    this.isPackSaved = false;

    this.validateTicket(this.selectedTickets)
  }

  /**
   * Projects ticket validation state into a signal.
   * Marks `ticketsValid` as `true` if no ticket has `hasError`, 
   * or if `hasError` is a falsey value.
   * 
   * @param tickets - The list of tickets to validate.
   */
  validateTicket(tickets: Ticket[]) {
    this.ticketsValid.set(!tickets.find(ticket => ticket.hasError));
  }

  /**
   * Add a  ticket to the departure from the seat options buttons
   * @return
   */
  public addTicketToDeparture(ticket_slug: string) {
    const ticketToAdd = {
      slug: ticket_slug,
      ticket_type: ticket_slug,
      ticket_name: this.departureSeatOptions.find(ticket => ticket.slug === ticket_slug)?.name || ''
    };

    this.addTicket(ticketToAdd);
  }


  /**
   * Reset the tickets array
   */
  public resetTickets() {

    // Put all the tickets from selectedTickets back into SeatOptions
    this.selectedTickets.forEach(ticket => {
      const originalTicket = this.departureSeatOptions.find(t => t.slug === ticket.ticket_type);
      if (originalTicket) {
        originalTicket.available_seats += 1;
      }
    });

    // Reset the departure seat options and selected tickets
    this.selectedTickets = [];
    this.tourForm.controls['tickets'].setValue(this.selectedTickets);

    this.isPackSaved = false;
  }


  /**
   * Update a ticket with form data
   * @param ticket 
   */
  public updateTicket(ticket: Ticket, ticketIndex: number) {
    // Update the ticket in the selectedTickets array and the form
    const originalTicketType = this.selectedTickets[ticketIndex].ticket_type;
    const newTicketType = ticket.ticket_type;

    // Update the ticket data
    this.selectedTickets[ticketIndex] = ticket;
    this.tourForm.controls['tickets'].setValue(this.selectedTickets);

    // Update available seats based on the changes in ticket counts
    if (originalTicketType !== newTicketType) {
      const originalTicket = this.departureSeatOptions.find(t => t.slug === originalTicketType);
      const newTicket = this.departureSeatOptions.find(t => t.slug === newTicketType);

      if (originalTicket) {
        originalTicket.available_seats += 1;
      }
      if (newTicket) {
        newTicket.available_seats -= 1;
      }
    }

    this.isPackSaved = false;

    this.validateTicket(this.selectedTickets)
  }


  /**
   * Delete a ticket
   * @param event 
   */
  public deleteTicket(index: number) {
    const ticket = this.selectedTickets[index];
    // Update the ticket in the selectedTickets array and the form
    this.selectedTickets.splice(index, 1);
    this.tourForm.controls['tickets'].setValue(this.selectedTickets);

    // Find the original ticket in departureSeatOptions and increment the stock
    const originalTicket = this.departureSeatOptions.find(t => t.slug === ticket.ticket_type);
    if (originalTicket) {
      originalTicket.available_seats += 1;
    }

    this.isPackSaved = false;
  }


  /**
   * Adds the selected tickets to a new pack and resets the form.
   * 
   * @returns {void}
   */
  public addToPacks() {
    if (this.selectedTickets.length === 0) {
      return;
    }

    const packId = crypto.randomUUID();
    const pack: Pack = {
      id: packId,
      name: this.packForm.controls['name'].value,
      origin: this.tour.title,
      tickets: [...this.selectedTickets],
      created_at: new Date(),
      owner_id: this.authService.user()?.ID,
      owner_name:this.authService.user()?.display_name
    };

    this.packService.addPack(pack);
    this.packForm.reset()
    this.isPackSaved = true;
  }


  public validatePack(packId: string) {
    const validation = this.packService.validatePackOnSeatOptions(packId, this.departureSeatOptions)
    if (validation === true) {
      return true
    } else {
      return false
    }
  }


  /**
   * Load a pack into the form
   * 
   * @param id The id of the pack to load
   * @returns {void}
   */
  public loadPack(id: string) {
    const pack = this.packService.getPackById(id);
    if (!pack) {
      console.error(`Pack with id ${id} not found`);
      return;
    }

    const validation = this.packService.validatePackOnSeatOptions(id, this.departureSeatOptions);
    const tickets = [...pack.tickets];

    // Reset previous errors
    tickets.forEach(ticket => {
      delete ticket.hasError;
      delete ticket.formOptions;
    });

    if (Array.isArray(validation)) {
      // Mark tickets with errors
      validation.forEach((error: { ticketType: string, difference: number }) => {
        const ticketsToUpdate = tickets.filter(ticket => ticket.ticket_type === error.ticketType).slice(error.difference);
        ticketsToUpdate.forEach(ticket => {
          ticket.hasError = true;
          ticket.formOptions = { seatOptions : this.departureSeatOptions };
        });
      });

      tickets.sort((a, b) => a.ticket_type.localeCompare(b.ticket_type));
    }

    this.resetTickets();
    tickets.forEach(ticket => {
      this.addTicket(ticket);
    });
  }

  
  /**
   * Check if a ticket is valid
   * If a ticket is deprecated, but present in the tour.valid_ticket_types array, it is still valid
   * @param ticket 
   * @return
   */
  public isValidTicket(ticket: any): boolean {
    return !ticket.depricated || 
      (this.tour.valid_ticket_types && 
       this.tour.valid_ticket_types.some(validTicket => validTicket.slug === ticket.slug));
  }
}
